import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HERO_CARDS, VALUES, COMPANY, STATS } from "../mock/mock";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CarCard from "../components/CarCard";
import SectionTitle from "../components/SectionTitle";
import TestimonialCarousel from "../components/TestimonialCarousel";
import Counter from "../components/Counter";
import {
  Building2,
  BadgeCheck,
  Handshake,
  ArrowLeftRight,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Search,
  Sparkles,
  Shield,
  ChevronDown,
} from "lucide-react";
import { carsApi, imageUrl } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

const iconMap = { Building2, BadgeCheck, Handshake, ArrowLeftRight };

const BRAND_LOGOS = [
  "BMW",
  "Mercedes-Benz",
  "Audi",
  "Porsche",
  "Tesla",
  "Land Rover",
  "Volkswagen",
  "Polestar",
];

const HERO_IMG = "/brand/hero.jpg";

export default function Home() {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    carsApi.list({ sort: "newest", light: 1 }).then((list) => {
      const readyCars = list.filter((c) => c.ready);
      setCars(readyCars.slice(0, 8));
      setTotal(list.length);
    });
  }, []);

  const heroOverlines = {
    STOCK: t("home.stockOverline"),
    "WIJ KOPEN UW AUTO": t("home.buyOverline"),
    "VERKOOP MIJN AUTO": t("home.sellOverline"),
  };
  const heroTitles = {
    STOCK: t("nav.offer").toUpperCase(),
    "WIJ KOPEN UW AUTO": t("nav.buyYourCar").toUpperCase(),
    "VERKOOP MIJN AUTO": t("nav.sellMyCar").toUpperCase(),
  };

  const valueTitles = [
    t("home.valueShowroomTitle"),
    t("home.valueQualityTitle"),
    t("home.valueAdviceTitle"),
    t("home.valueTradeTitle"),
  ];
  const valueTexts = [
    t("home.valueShowroomText"),
    t("home.valueQualityText"),
    t("home.valueAdviceText"),
    t("home.valueTradeText"),
  ];
  const aboutPoints = [
    t("home.aboutPoint1"),
    t("home.aboutPoint2"),
    t("home.aboutPoint3"),
    t("home.aboutPoint4"),
  ];

  return (
    <div className="bg-[#1a1a1a] text-white overflow-hidden">
      <Navbar />

      {/* Cinematic Hero */}
      <section className="relative min-h-[56vh] flex items-center pt-[80px]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="TR Premium Cars showroom" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/80 to-[#1a1a1a]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent" />
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#c5a76f]/10 border border-[#c5a76f]/30 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#c5a76f]" />
              <span className="text-[#c5a76f] text-xs tracking-[0.25em] uppercase font-medium">
                {t("home.badge")}
              </span>
            </div>
            <h1
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[0.98] mb-4"
              style={{ fontFamily: "'Familjen Grotesk', serif" }}
            >
              {t("home.heroLine1")}
              <br />
              <span className="text-[#c5a76f] italic">{t("home.heroLine2")}</span>
              <br />
              {t("home.heroLine3")}
            </h1>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-6 max-w-xl">
              {t("home.heroSubtitle1")}{" "}
              <span className="text-[#c5a76f] font-semibold">{t("home.heroSubtitle2")}</span>{" "}
              {t("home.heroSubtitle3")}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/stock"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#c5a76f] text-black font-semibold text-sm tracking-widest uppercase hover:bg-[#d8bd86] transition-colors rounded"
              >
                <Search className="w-4 h-4" />
                {t("home.seeStock")}
                <span className="bg-black/20 text-[#c5a76f] px-2 py-0.5 rounded text-xs ml-1">
                  {total || "..."}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${COMPANY.phone}`}
                className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 text-white hover:border-[#c5a76f] hover:text-[#c5a76f] transition-colors text-sm font-semibold tracking-widest uppercase rounded"
              >
                <Phone className="w-4 h-4" />
                {COMPANY.phone}
              </a>
            </div>

            <div className="flex flex-wrap gap-8 mt-7 pt-6 border-t border-white/10">
              {[
                { val: STATS.years, suffix: "+", label: t("home.statsYears") },
                { val: STATS.carsSold, suffix: "+", label: t("home.statsSold") },
                { val: 600, suffix: " m²", label: t("home.statsShowroom") },
                { val: 12, suffix: ` ${t("home.months")}`, label: t("home.statsWarranty") },
              ].map((s, i) => (
                <div key={i}>
                  <div
                    className="text-3xl md:text-4xl font-bold text-[#c5a76f]"
                    style={{ fontFamily: "'Familjen Grotesk', serif" }}
                  >
                    <Counter to={s.val} />
                    {s.suffix}
                  </div>
                  <div className="text-white/55 text-xs tracking-widest uppercase mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#c5a76f]/60 hidden md:flex flex-col items-center gap-2 animate-bounce">
          <span className="text-[10px] tracking-[0.3em] uppercase">{t("home.scroll")}</span>
          <ChevronDown className="w-5 h-5" />
        </div>
      </section>

      {/* Premium wagens */}
      <section className="py-24 px-5 lg:px-8 bg-[#1a1a1a]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[#c5a76f] text-sm tracking-[0.25em] uppercase font-medium mb-3">
                {t("home.premiumOverline")}
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold text-white"
                style={{ fontFamily: "'Familjen Grotesk', serif" }}
              >
                {t("home.premiumTitle")}{" "}
                <span className="text-[#c5a76f]">{t("home.premiumTitleAccent")}</span>
              </h2>
            </div>
            <Link
              to="/stock"
              className="hidden md:inline-flex items-center gap-2 text-[#c5a76f] hover:text-[#d8bd86] text-sm font-medium tracking-widest uppercase group"
            >
              {t("home.fullOffer")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {cars.length === 0 ? (
            <div className="text-center text-white/40 py-20">{t("common.loading")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cars.map((car, i) => (
                <div key={car.id} className={i >= 4 ? "hidden sm:block" : ""}>
                  <CarCard car={car} />
                </div>
              ))}
            </div>
          )}

          <div className="mt-14 text-center">
            <Link
              to="/stock"
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-transparent border border-[#c5a76f] text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors text-sm font-medium tracking-widest uppercase"
            >
              {t("home.viewCars", { n: total || "" })} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Over ons */}
      <section className="py-24 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#c5a76f]/30 rounded-md" />
            <img src={HERO_IMG} alt="Over ons" className="relative w-full rounded-md aspect-[4/3] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#c5a76f] text-black px-7 py-5 rounded-md shadow-xl hidden md:block">
              <div className="text-4xl font-bold" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                <Counter to={STATS.years} />+
              </div>
              <div className="text-xs tracking-widest uppercase font-semibold">
                {t("home.yearsExp")}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[#c5a76f] text-sm tracking-[0.25em] uppercase mb-3">
              {t("home.welcomeAt")}
            </div>
            <h2
              className="text-4xl md:text-5xl text-white font-bold mb-6"
              style={{ fontFamily: "'Familjen Grotesk', serif" }}
            >
              {t("home.aboutTitle")} <span className="text-[#c5a76f]">{t("home.aboutTitleAccent")}</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">{t("home.aboutText")}</p>
            <ul className="space-y-4 mb-8">
              {aboutPoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <BadgeCheck className="w-5 h-5 text-[#c5a76f] mt-0.5 flex-shrink-0" />
                  <span className="text-white/85">{p}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/over-ons"
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-medium tracking-widest uppercase rounded"
            >
              {t("home.aboutCta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 3 Service Cards */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {HERO_CARDS.map((c) => (
            <Link
              key={c.title}
              to={c.href}
              className="relative group overflow-hidden block aspect-[16/9] md:aspect-[4/3] lg:aspect-[5/3]"
            >
              <img
                src={imageUrl(c.image)}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20 group-hover:from-black/90 transition-colors" />
              <div className="relative h-full flex items-end p-8 lg:p-12">
                <div>
                  <div className="text-[#c5a76f] text-xs tracking-[0.3em] uppercase font-medium mb-2 opacity-80">
                    {heroOverlines[c.title]}
                  </div>
                  <h2
                    className="text-white text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
                    style={{ fontFamily: "'Familjen Grotesk', serif" }}
                  >
                    {heroTitles[c.title]}
                  </h2>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#c5a76f] text-sm tracking-widest font-medium opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                    {t("common.discover")} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Brand strip */}
      <section className="py-14 px-5 lg:px-8 bg-[#141414] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center text-white/40 text-xs tracking-[0.3em] uppercase mb-8">
            {t("home.brandsLine")}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 md:gap-x-14">
            {BRAND_LOGOS.map((b) => (
              <span
                key={b}
                className="text-white/40 hover:text-[#c5a76f] transition-colors text-lg md:text-xl font-medium tracking-wider"
                style={{ fontFamily: "'Familjen Grotesk', serif" }}
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-5 lg:px-8 bg-[#141414]">
        <div className="max-w-[1400px] mx-auto">
          <SectionTitle overline={t("home.valuesOverline")} title={t("home.valuesTitle")} />
          <p className="text-center text-white/65 max-w-2xl mx-auto -mt-6 mb-16">
            {t("home.valuesText")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = iconMap[v.icon];
              return (
                <div
                  key={i}
                  className="group p-8 bg-[#242424] border border-white/5 rounded-md hover:border-[#c5a76f]/40 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-full bg-[#c5a76f]/10 border border-[#c5a76f]/30 flex items-center justify-center mb-6 group-hover:bg-[#c5a76f] transition-colors">
                    <Icon className="w-6 h-6 text-[#c5a76f] group-hover:text-black transition-colors" />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-3">{valueTitles[i]}</h3>
                  <p className="text-white/65 text-sm leading-relaxed">{valueTexts[i]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto">
          <SectionTitle
            overline={t("home.testimonialsOverline")}
            title={t("home.testimonialsTitle")}
          />
          <TestimonialCarousel />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="px-5 lg:px-8 pb-20">
        <div className="max-w-[1400px] mx-auto relative overflow-hidden rounded-md">
          <div className="absolute inset-0">
            <img
              src="/brand/showroom.jpg"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/80 to-[#1a1a1a]/50" />
          </div>
          <div className="relative px-8 md:px-16 py-16 md:py-24 max-w-2xl">
            <Shield className="w-10 h-10 text-[#c5a76f] mb-5" />
            <h2
              className="text-3xl md:text-5xl font-bold text-white mb-5"
              style={{ fontFamily: "'Familjen Grotesk', serif" }}
            >
              {t("home.ctaTitle1")}{" "}
              <span className="text-[#c5a76f] italic">{t("home.ctaTitleAccent")}</span>{" "}
              {t("home.ctaTitle2")}
            </h2>
            <p className="text-white/75 text-lg mb-8">{t("home.ctaText")}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#c5a76f] text-black font-semibold hover:bg-[#d8bd86] transition-colors text-sm tracking-widest uppercase rounded"
              >
                {t("home.makeAppointment")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/stock"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white hover:border-[#c5a76f] hover:text-[#c5a76f] transition-colors text-sm tracking-widest uppercase rounded"
              >
                {t("home.viewCarsShort")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <SectionTitle overline={t("common.contactUs")} title={COMPANY.owner} align="left" />
            <div className="space-y-5 mt-2">
              <a
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-4 text-white hover:text-[#c5a76f] transition-colors group"
              >
                <div className="w-12 h-12 rounded-full border border-[#c5a76f]/40 flex items-center justify-center group-hover:bg-[#c5a76f] group-hover:border-[#c5a76f] transition-colors">
                  <Phone className="w-5 h-5 text-[#c5a76f] group-hover:text-black" />
                </div>
                <span className="text-lg">{COMPANY.phone}</span>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 text-white hover:text-[#c5a76f] transition-colors group"
              >
                <div className="w-12 h-12 rounded-full border border-[#c5a76f]/40 flex items-center justify-center group-hover:bg-[#c5a76f] group-hover:border-[#c5a76f] transition-colors">
                  <Mail className="w-5 h-5 text-[#c5a76f] group-hover:text-black" />
                </div>
                <span className="text-lg">{COMPANY.email}</span>
              </a>
              <div className="flex items-start gap-4 text-white">
                <div className="w-12 h-12 rounded-full border border-[#c5a76f]/40 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#c5a76f]" />
                </div>
                <div>
                  <div className="text-lg">{COMPANY.address.street}</div>
                  <div className="text-white/65">
                    {COMPANY.address.zip} {COMPANY.address.city}, {COMPANY.address.country}
                  </div>
                  <div className="text-[#c5a76f] text-sm mt-1">{t("common.onAppointment")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-md overflow-hidden border border-white/10 h-[420px]">
            <iframe
              title="TR Premium Cars Showroom"
              src="https://maps.google.com/maps?q=Kleiweg%2027%209340%20Lede%20Belgium&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full grayscale-[40%] contrast-110"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
